import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { TabNavigation } from "../components/ui/tab-navigation";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  ArrowLeft,
  Search,
  Plus,
  Pin,
  Reply,
  Send,
  Paperclip,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  Megaphone,
  MessageSquare,
  X,
  Mail,
  AlertCircle,
  BookOpen,
  Calendar,
  FileText,
  Target,
  Users,
  Zap,
} from "lucide-react";
import { useAuth } from "../App";
import { ROLES, UserRole } from "../constant/roles";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { cn } from "../lib/utils";
import { Switch } from "@radix-ui/react-switch";
import { toast } from "sonner";
import { MessagesTab } from "../components/MessagesTab";

// API imports
import {
  useCreateAnnouncementMutation,
  useAnnouncementsListQuery,
  useCourseProjectsQuery,
  useMarkAnnouncementReadMutation,
  type CreateAnnouncementRequest,
  type GetAnnouncementsParams,
} from "../api/CommunicationApi";
import { useCoursesQuery } from "../api/createProjectApi";
import { useNavigate, useLocation } from "react-router-dom";

// Types adapted to existing UI without altering UI props/structure
interface Announcement {
  id: string;
  title: string;
  content: string;
  authorId: string;
  authorName: string;
  authorRole: UserRole;
  courseId: string;
  courseName: string;
  createdAt: string;
  isPinned: boolean;
  visibility: "course" | "section" | "group";
  targetGroup?: string;
  allowComments: boolean;
  comments: Comment[];
  isRead: boolean;
}

interface Comment {
  id: string;
  authorId: string;
  authorName: string;
  authorRole: UserRole;
  content: string;
  createdAt: string;
  parentId?: string;
  replies?: Comment[];
}

// Utilities
function timeAgo(date: string) {
  const now = new Date();
  const past = new Date(date);
  const diffInHours = Math.floor(
    (now.getTime() - past.getTime()) / (1000 * 60 * 60),
  );
  if (diffInHours < 1) return "Less than an hour ago";
  if (diffInHours < 24) return `${diffInHours} hours ago`;
  return `${Math.floor(diffInHours / 24)} days ago`;
}

function AnnouncementCard({
  announcement,
  onClick,
  isHighlighted,
}: {
  announcement: Announcement;
  onClick?: (a: Announcement) => void;
  isHighlighted?: boolean;
}) {
  const { user } = useAuth();
  const isUnread = !announcement.isRead;

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={() => onClick?.(announcement)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick?.(announcement);
      }}
      className={cn(
        "flex flex-col gap-1 py-3 pl-3 border rounded-md cursor-pointer transition-colors hover:bg-gray-50",
        isHighlighted ? " bg-gray-100" : "",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-base text-bits-blue-neutral-900 font-semibold line-clamp-1">
              {announcement.title}
            </span>
          </div>
          {/* up to 2 lines of content preview */}
          <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
            {announcement.content}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="flex items-center text-[10px] text-gray-500 p-3">
            {timeAgo(announcement.createdAt)}
            {!announcement.isRead && announcement.authorId !== user?.id && (
              <div className="w-2 h-2 ml-1 bg-red-500 rounded-full shrink-0" />
            )}
          </span>
        </div>
      </div>
    </Card>
  );
}

// AdvancedMessageComposer refactored to wire APIs under the hood without UI change
type TargetAudienceUI =
  | "all-students"
  | "course-students"
  | "project-students"
  | "pending-submissions";

type PriorityUI = "low" | "normal" | "high" | "urgent";

function AdvancedMessageComposer({
  onSave,
}: {
  onSave: (message: any) => void;
}) {
  const { user } = useAuth();

  // Dialog state
  const [open, setOpen] = useState(false);

  // Form state (kept same field names to avoid UI changes)
  const [formData, setFormData] = useState<{
    messageType: "message" | "announcement";
    targetAudience: TargetAudienceUI;
    selectedCourse?: string;
    selectedProject?: string;
    subject: string;
    content: string;
    priority: PriorityUI;
    requireAcknowledgment: boolean;
    scheduleDelivery: boolean;
    scheduledDate?: string;
    scheduledTime?: string;
    attachments?: File[];
  }>({
    messageType: "announcement",
    targetAudience: "all-students",
    selectedCourse: "",
    selectedProject: "",
    subject: "",
    content: "",
    priority: "normal",
    requireAcknowledgment: false,
    scheduleDelivery: false,
    scheduledDate: "",
    scheduledTime: "",
    attachments: [],
  });

  // Real API data for courses and projects
  const { data: coursesResp, isLoading: coursesLoading } =
    useCoursesQuery(true);
  const courses = useMemo(() => {
    const list = (coursesResp || []) as Array<{
      id: string;
      name: string;
      code: string;
      term: string;
      academicYear: string;
      startDate: string;
      endDate: string;
    }>;
    return list.map((c) => ({
      id: c.id,
      name: c.name,
      students: undefined, // unknown from API; not displayed elsewhere numerically
    }));
  }, [coursesResp]);

  const selectedCourseId = formData.selectedCourse || "";
  const {
    data: projectsResp,
    isLoading: projectsLoading,
    refetch: refetchProjects,
  } = useCourseProjectsQuery(selectedCourseId, !!selectedCourseId);

  const projects = useMemo(() => {
    const projectsData =
      (projectsResp as any)?.data?.projects ||
      (projectsResp as any)?.data?.data?.projects ||
      [];
    return (
      projectsData as Array<{ id: string; title: string; projectId: string }>
    ).map((p) => ({
      id: p.id,
      name: p.title,
      totalStudents: undefined,
      pendingSubmissions: undefined,
    }));
  }, [projectsResp]);

  useEffect(() => {
    if (selectedCourseId) {
      refetchProjects();
    }
  }, [selectedCourseId, refetchProjects]);

  // Create Announcement mutation
  const { mutateAsync: createAnnouncement, isPending: creating } =
    useCreateAnnouncementMutation();

  // Map UI target to backend targetAudience enum and required IDs
  function toBackendCreatePayload(): CreateAnnouncementRequest {
    const base: CreateAnnouncementRequest = {
      title: formData.subject || "",
      content: formData.content || "",
      targetAudience: ["all"],
    };

    // Map UI target
    switch (formData.targetAudience) {
      case "all-students":
        base.targetAudience[0] = "all";
        break;
      case "course-students":
        base.targetAudience[0] = "student_specific_course";
        base.courseId = formData.selectedCourse || null;
        break;
      case "project-students":
        base.targetAudience[0] = "student_specific_project";
        base.courseId = formData.selectedCourse || null;
        base.projectId = formData.selectedProject || null;
        break;
      case "pending-submissions":
        base.targetAudience[0] = "student_pending_submission";
        base.courseId = formData.selectedCourse || null;
        base.projectId = formData.selectedProject || null;
        break;
      default:
        base.targetAudience[0] = "all";
    }

    // Priority mapping
    if (
      formData.priority === "low" ||
      formData.priority === "normal" ||
      formData.priority === "high"
    ) {
      base.priority = formData.priority as "low" | "normal" | "high";
    } else {
      base.priority = "high";
    }

    // Optional schedule
    if (formData.scheduleDelivery && formData.scheduledDate) {
      const date = formData.scheduledDate;
      const time = formData.scheduledTime || "09:00";
      const iso = new Date(`${date}T${time}:00.000Z`).toISOString();
      base.scheduledFor = iso;
    }

    // Choose announcementType: preserve general unless project students (projectupdate)
    if (
      base.targetAudience[0] === "studentspecificproject" ||
      base.targetAudience[0] === "studentpendingsubmission"
    ) {
      base.announcementType = "projectupdate";
    } else {
      base.announcementType = "general";
    }

    return base;
  }

  const getRecipientCount = () => {
    switch (formData.targetAudience) {
      case "all-students":
        return 0;
      case "course-students":
        return 0;
      case "project-students":
        return 0;
      case "pending-submissions":
        return 0;
      default:
        return 0;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.messageType !== "announcement") {
      toast.error("Only announcement creation is enabled in this screen.");
      return;
    }

    // Validate required selections based on target
    if (
      formData.targetAudience === "course-students" &&
      !formData.selectedCourse
    ) {
      toast.error("Please select a course.");
      return;
    }
    if (
      (formData.targetAudience === "project-students" ||
        formData.targetAudience === "pending-submissions") &&
      (!formData.selectedCourse || !formData.selectedProject)
    ) {
      toast.error("Please select both a course and a project.");
      return;
    }

    try {
      const payload = toBackendCreatePayload();
      const res = await createAnnouncement(payload);
      if (res?.data?.id || (res as any)?.data?.data?.id) {
        toast.success("Announcement created successfully.");
      } else {
        toast.success("Announcement request submitted.");
      }

      const recipientCount = getRecipientCount();
      onSave({
        ...formData,
        id: Date.now().toString(),
        authorId: user?.id,
        authorName: user?.name,
        authorRole: user?.role,
        createdAt: new Date().toISOString(),
        recipientCount,
        status: formData.scheduleDelivery ? "scheduled" : "sent",
      });
      setOpen(false);

      // Reset minimal fields
      setFormData({
        messageType: "announcement",
        targetAudience: "all-students",
        selectedCourse: "",
        selectedProject: "",
        subject: "",
        content: "",
        priority: "normal",
        requireAcknowledgment: false,
        scheduleDelivery: false,
        scheduledDate: "",
        scheduledTime: "",
        attachments: [],
      });
    } catch (err: any) {
      toast.error(err?.message || "Failed to create announcement.");
    }
  };

  if (user?.role === ROLES.STUDENT) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="bits" className="bg-bits-blue hover:bg-bits-blue/90">
          <Send className="h-4 w-4 mr-2" />
          Compose
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Mail className="h-5 w-5 text-bits-blue" />
            <span>Compose New Announcement</span>
          </DialogTitle>
          <DialogDescription>
            Send targeted announcements to students
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
            <Label className="text-sm font-semibold flex items-center space-x-2">
              <Target className="h-4 w-4" />
              <span>Target Audience</span>
            </Label>

            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3">
                <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-white">
                  <input
                    type="radio"
                    name="targetAudience"
                    value="all-students"
                    checked={formData.targetAudience === "all-students"}
                    onChange={(e) =>
                      setFormData((f) => ({
                        ...f,
                        targetAudience: e.target.value as TargetAudienceUI,
                      }))
                    }
                    className="text-bits-blue"
                  />
                  <Users className="h-5 w-5 text-bits-blue" />
                  <div className="flex-1">
                    <div className="font-medium">All Students</div>
                    <div className="text-sm text-muted-foreground">
                      Send to all students across all courses
                    </div>
                  </div>
                </label>

                <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-white">
                  <input
                    type="radio"
                    name="targetAudience"
                    value="course-students"
                    checked={formData.targetAudience === "course-students"}
                    onChange={(e) =>
                      setFormData((f) => ({
                        ...f,
                        targetAudience: e.target.value as TargetAudienceUI,
                      }))
                    }
                    className="text-bits-blue"
                  />
                  <BookOpen className="h-5 w-5 text-orange-500" />
                  <div className="flex-1">
                    <div className="font-medium">
                      Students in Specific Course
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Send to all students enrolled in a specific course
                    </div>
                  </div>
                </label>

                <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-white">
                  <input
                    type="radio"
                    name="targetAudience"
                    value="project-students"
                    checked={formData.targetAudience === "project-students"}
                    onChange={(e) =>
                      setFormData((f) => ({
                        ...f,
                        targetAudience: e.target.value as TargetAudienceUI,
                      }))
                    }
                    className="text-bits-blue"
                  />
                  <FileText className="h-5 w-5 text-green-500" />
                  <div className="flex-1">
                    <div className="font-medium">
                      Students in Specific Project
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Send to all students assigned to a specific project
                    </div>
                  </div>
                </label>

                <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-white">
                  <input
                    type="radio"
                    name="targetAudience"
                    value="pending-submissions"
                    checked={formData.targetAudience === "pending-submissions"}
                    onChange={(e) =>
                      setFormData((f) => ({
                        ...f,
                        targetAudience: e.target.value as TargetAudienceUI,
                      }))
                    }
                    className="text-bits-blue"
                  />
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <div className="flex-1">
                    <div className="font-medium">
                      Students with Pending Submissions
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Send only to students who haven't submitted their project
                      yet
                    </div>
                  </div>
                </label>
              </div>

              {(formData.targetAudience === "course-students" ||
                formData.targetAudience === "project-students" ||
                formData.targetAudience === "pending-submissions") && (
                <div className="space-y-3 ml-8">
                  <div className="space-y-2">
                    <Label htmlFor="course-select">Select Course</Label>
                    <Select
                      value={formData.selectedCourse ?? ""}
                      onValueChange={(value) =>
                        setFormData((f) => ({
                          ...f,
                          selectedCourse: value,
                          selectedProject: "",
                        }))
                      }
                    >
                      <SelectTrigger id="course-select">
                        <SelectValue
                          placeholder={
                            coursesLoading ? "Loading..." : "Choose a course"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map((course) => (
                          <SelectItem key={course.id} value={course.id}>
                            <div className="flex justify-between items-center w-full">
                              <span>{course.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {(formData.targetAudience === "project-students" ||
                    formData.targetAudience === "pending-submissions") &&
                    formData.selectedCourse && (
                      <div className="space-y-2">
                        <Label htmlFor="project-select">Select Project</Label>
                        <Select
                          value={
                            formData.selectedProject
                              ? formData.selectedProject
                              : ""
                          }
                          onValueChange={(value) =>
                            setFormData((f) => ({
                              ...f,
                              selectedProject: value,
                            }))
                          }
                        >
                          <SelectTrigger id="project-select">
                            <SelectValue
                              placeholder={
                                projectsLoading
                                  ? "Loading..."
                                  : "Choose a project"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {projects.map((project) => (
                              <SelectItem key={project.id} value={project.id}>
                                <div className="flex justify-between items-center w-full">
                                  <span>{project.name}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>

          {/* <div className="p-3 bg-bits-blue/10 rounded-lg border border-bits-blue/20">
            <div className="flex items-center space-x-2 text-bits-blue">
              <Users className="h-4 w-4" />
              <span className="font-medium">
                This message will be sent to {getRecipientCount()} recipient
                {getRecipientCount() !== 1 ? "s" : ""}
              </span>
            </div>
          </div> */}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, subject: e.target.value }))
                }
                placeholder={
                  formData.messageType === "announcement"
                    ? "Announcement title..."
                    : "Message subject..."
                }
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">
                {formData.messageType === "announcement"
                  ? "Announcement Content"
                  : "Message Content"}
              </Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, content: e.target.value }))
                }
                placeholder={
                  formData.messageType === "announcement"
                    ? "Write your announcement..."
                    : "Type your message..."
                }
                rows={8}
                required
              />
            </div>
          </div>

          <div className="flex justify-between items-center pt-4">
            <div className="flex w-full space-x-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="bits"
                className="flex-1 bg-bits-blue hover:bg-bits-blue/90"
                disabled={creating}
              >
                {formData.scheduleDelivery ? (
                  <>
                    <Calendar className="h-4 w-4 mr-2" />
                    Schedule
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    {formData.messageType === "announcement"
                      ? "Send Announcement"
                      : "Send Message"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// In CommunicationPage.tsx, replace the existing AnnouncementsTab definition with this one.

function AnnouncementsTab({ announcementId }: { announcementId?: string }) {
  const [page, setPage] = React.useState<number>(1);
  const [limit, setLimit] = React.useState<number>(20);

  const params: GetAnnouncementsParams = React.useMemo(
    () => ({
      page,
      limit,
    }),
    [page, limit],
  );

  const announcementsTabActive = true;
  const { data, isLoading, isError, refetch } = useAnnouncementsListQuery(
    { page, limit },
    {
      enabled: announcementsTabActive,
      refetchInterval: announcementsTabActive ? 10000 : false,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
      staleTime: 0,
    },
  );

  const announcements: Announcement[] = React.useMemo(() => {
    const list =
      (data as any)?.data?.announcements ??
      (data as any)?.data?.data?.announcements ??
      [];
    return (list as any[]).map((a: any) => {
      const courseName = a?.course?.name ?? "";
      const authorName = a?.creator?.name ?? ROLES.INSTRUCTOR;
      return {
        id: a.id,
        title: a.title,
        content: a.content,
        authorId: a?.creator?.id ?? "",
        authorName,
        authorRole: ROLES.INSTRUCTOR as UserRole,
        courseId: a?.course?.id ?? "",
        courseName,
        createdAt: a.createdAt,
        isPinned: !!a.isPinned,
        visibility: "course",
        allowComments: false,
        comments: [],
        isRead: a.isRead,
      } as Announcement;
    });
  }, [data]);

  const { mutateAsync: markAnnouncementRead } =
    useMarkAnnouncementReadMutation();
  const [selectedAnnouncementId, setSelectedAnnouncementId] = React.useState<
    string | undefined
  >(undefined);

  React.useEffect(() => {
    if (announcementId) {
      setSelectedAnnouncementId(announcementId);
      return;
    }
    if (!isLoading && announcements.length > 0 && !selectedAnnouncementId) {
      setSelectedAnnouncementId(announcements[0]?.id);
    }
  }, [announcementId, isLoading, announcements, selectedAnnouncementId]);

  const onAnnouncementClick = async (a: Announcement) => {
    setSelectedAnnouncementId(a.id);

    if (!a.isRead) {
      try {
        await markAnnouncementRead(a.id);
      } catch (e) {
        toast.error("Failed to mark announcement as read.");
      }
    }
  };

  const { totalItems, totalPages } = React.useMemo(() => {
    const p =
      (data as any)?.data?.pagination ??
      (data as any)?.data?.data?.pagination ??
      null;
    return {
      totalItems: p?.total ?? announcements.length,
      totalPages: p?.pages ?? 1,
    };
  }, [data, announcements.length]);

  const selectedAnnouncement = React.useMemo(
    () => announcements.find((a) => a.id === selectedAnnouncementId),
    [announcements, selectedAnnouncementId],
  );

  return (
    <Card className="p-4 flex flex-col gap-4 h-[calc(100vh-180px)]">
      <div className="flex items-center justify-between ">
        <h2 className="text-xl font-semibold">
          Announcements {isLoading ? "loading..." : `(${announcements.length})`}
        </h2>
        <AdvancedMessageComposer onSave={() => refetch()} />
      </div>

      <div className="flex flex-1 min-h-0 gap-6 justify-between overflow-hidden">
        {/* LEFT LIST PANEL */}
        <div className="w-1/3 flex border rounded-md flex-col min-w-[260px]">
          <div className="flex-1 overflow-auto custom-scroll">
            {isLoading && (
              <div className="p-4 text-sm text-gray-500">
                Loading announcements...
              </div>
            )}

            {isError && !isLoading && (
              <div className="p-4 text-sm text-red-500">
                Failed to load announcements.
              </div>
            )}

            {!isLoading && announcements.length === 0 && !isError && (
              <div className="p-4 text-sm text-gray-500">
                No announcements found.
              </div>
            )}

            {announcements.map((announcement) => (
              <AnnouncementCard
                key={announcement.id}
                announcement={announcement}
                onClick={onAnnouncementClick}
                isHighlighted={announcement.id === selectedAnnouncementId}
              />
            ))}
          </div>

          {/* Pagination kept as requested */}
          {totalItems > 0 && (
            <div className="flex items-center border-t justify-between p-2 text-xs">
              <span>
                Page {page} of {Math.max(1, totalPages)} • {totalItems} items
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || isLoading}
                  onClick={() => setPage(Math.max(1, page - 1))}
                >
                  Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= Math.max(1, totalPages) || isLoading}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>

                <Select
                  value={String(limit)}
                  onValueChange={(v) => {
                    setLimit(Number(v));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="h-8 w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 20, 30, 50].map((l) => (
                      <SelectItem key={l} value={String(l)}>
                        {l} / page
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT DETAIL PANEL */}
        <div className="flex border rounded-md flex-1 flex-col min-w-0">
          {selectedAnnouncement ? (
            <div className="flex-1 overflow-auto custom-scroll p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold">
                    {selectedAnnouncement.title}
                  </h3>
                  <div className="mt-1 text-sm text-muted-foreground flex flex-wrap items-center gap-1">
                    <span>{selectedAnnouncement.authorName}</span>
                    {selectedAnnouncement.courseName && (
                      <>
                        <span>•</span>
                        <span>{selectedAnnouncement.courseName}</span>
                      </>
                    )}
                    <span>•</span>
                    <span>{timeAgo(selectedAnnouncement.createdAt)}</span>
                  </div>
                </div>
              </div>
              <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm bg-background p-4 rounded-lg">
                {selectedAnnouncement.content}
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-500">
              Select an announcement to view details.
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function CommunicationPage({
  defaultTab = "announcements",
}: {
  defaultTab?: "announcements" | "messages";
}) {
  const location = useLocation() as any;
  const navState = (location.state ?? {}) as {
    defaultTab?: "announcements" | "messages";
    announcementId?: string;
    conversationId?: string;
    from?: string;
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const mode =
    (searchParams.get("mode") as "announcements" | "messages") || defaultTab;
  const compose = searchParams.get("compose") === "true";
  const [tab, setTab] = useState<"announcements" | "messages">(mode);
  const [announcementId, setAnnouncementId] = useState<string | undefined>(
    navState.announcementId,
  );
  const [conversationId, setConversationId] = useState<string | undefined>(
    navState.conversationId,
  );
  const navigate = useNavigate();

  // Update tab when URL parameter changes
  useEffect(() => {
    setTab(mode);
  }, [mode]);

  function goBack() {
    // If we have a 'from' path in state, go there. Otherwise go back.
    if (navState.from) {
      navigate(navState.from);
    } else {
      navigate(-1);
    }
  }

  useEffect(() => {
    if (navState.defaultTab) setTab(navState.defaultTab);
    if (navState.announcementId) setAnnouncementId(navState.announcementId);
    if (navState.conversationId) setConversationId(navState.conversationId);
  }, [navState.defaultTab, navState.announcementId, navState.conversationId]);

  return (
    <div className="container mx-auto mt-4 p-4">
      <div className="flex flex-row items-center space-x-2 mb-4">
        <button onClick={goBack} className="p-2 rounded-full hover:bg-gray-100">
          <ArrowLeft className="h-7 w-8 text-gray-700" />
        </button>
        <h1 className="text-2xl font-bold">Notifications check</h1>
      </div>

      <div className="mx-2 space-x-4">
        <div className="p-0 -mt-2 -mx-2">
          <TabNavigation
            tabs={[
              {
                key: "announcements",
                label: "Announcements",
                icon: <Megaphone className="h-4 w-4" />,
              },
              {
                key: "messages",
                label: "Messages",
                icon: <MessageSquare className="h-4 w-4" />,
              },
            ]}
            activeTab={tab}
            onTabChange={(selectedTab) => {
              setTab(selectedTab as "announcements" | "messages");
              // Use replace: true so tab switches don't add to browser history
              setSearchParams({ mode: selectedTab }, { replace: true });
            }}
            className="mb-6 mx-2"
          />
        </div>

        <div className="p-0 mt-6">
          {tab === "announcements" && (
            <AnnouncementsTab announcementId={announcementId || ""} />
          )}
          {tab === "messages" && (
            <MessagesTab
              initialThreadId={conversationId || ""}
              initialCompose={compose}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default CommunicationPage;
